import { useTranslation } from '@kgc/i18n';
import { Progress } from '@kgc/ui';
import { useMemo } from 'react';
import type { TestResultData } from '../hooks/useTestResults';

interface TestingDashboardHeaderProps {
  totalFeatures: number;
  allResults: TestResultData[];
}

/**
 * Release testing dashboard summary header.
 *
 * Performance note: all 6 derived values (passed/failed/tested/notTested
 * counts, progress %, tester list, last activity) are computed in ONE
 * memoized pass over `allResults`. Previously each was a separate
 * `.filter()` + `.map()` + `.reduce()` call running on every parent render,
 * which became O(n*6) over hundreds of test results. The single-pass
 * reducer is O(n) and `useMemo` prevents re-computation unless the
 * underlying arrays actually change.
 */
export function TestingDashboardHeader({ totalFeatures, allResults }: TestingDashboardHeaderProps) {
  const { t } = useTranslation();

  const stats = useMemo(() => {
    let passedCount = 0;
    let failedCount = 0;
    const testers = new Set<string>();
    let lastResult: TestResultData | null = null;
    let lastResultTime = 0;

    for (const r of allResults) {
      if (r.status === 'passed') passedCount++;
      else if (r.status === 'failed') failedCount++;

      if (r.status !== 'not_tested') {
        testers.add(r.testerName);
      }

      // Single `Date.parse` per row instead of `new Date()` per comparison.
      const t = Date.parse(r.updatedAt);
      if (!Number.isNaN(t) && t > lastResultTime) {
        lastResultTime = t;
        lastResult = r;
      }
    }

    const testedCount = passedCount + failedCount;
    const notTestedCount = Math.max(0, totalFeatures - testedCount);
    const progressPct = totalFeatures > 0 ? Math.round((testedCount / totalFeatures) * 100) : 0;
    const testerNames = Array.from(testers);

    return {
      passedCount,
      failedCount,
      testedCount,
      notTestedCount,
      progressPct,
      testerNames,
      lastResult,
    };
  }, [allResults, totalFeatures]);

  const {
    passedCount,
    failedCount,
    testedCount,
    notTestedCount,
    progressPct,
    testerNames,
    lastResult,
  } = stats;

  return (
    <div className="flex flex-wrap items-center gap-6 rounded-lg border border-border bg-card p-5">
      {/* Stat: total */}
      <div className="text-center">
        <div className="text-3xl font-bold text-foreground">{totalFeatures}</div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('releaseHub.testing.summary')}
        </div>
      </div>

      {/* Stat: passed */}
      <div className="text-center">
        <div className="text-3xl font-bold text-green-500">{passedCount}</div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('releaseHub.testing.status.passed')}
        </div>
      </div>

      {/* Stat: failed */}
      <div className="text-center">
        <div className="text-3xl font-bold text-red-500">{failedCount}</div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('releaseHub.testing.status.failed')}
        </div>
      </div>

      {/* Stat: untested */}
      <div className="text-center">
        <div className="text-3xl font-bold text-muted-foreground">{notTestedCount}</div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('releaseHub.testing.untested')}
        </div>
      </div>

      {/* Progress + team info */}
      <div className="min-w-[200px] flex-1">
        <Progress value={progressPct} className="h-2" />
        <p className="mt-1.5 text-xs text-muted-foreground">
          {t('releaseHub.testing.testedPct', {
            done: testedCount,
            total: totalFeatures,
            pct: progressPct,
          })}
        </p>

        {/* Team info */}
        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          {testerNames.slice(0, 3).map(name => (
            <span
              key={name}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-600 text-[9px] font-semibold text-white"
              title={name}
            >
              {name.charAt(0).toUpperCase()}
            </span>
          ))}
          {testerNames.length > 0 && (
            <span>
              {testerNames.length} {t('releaseHub.testing.testers')}
              {lastResult && (
                <>
                  {' · '}
                  {t('releaseHub.testing.lastTest')}: {lastResult.testerName},{' '}
                  {new Date(lastResult.updatedAt).toLocaleDateString('hu-HU')}
                </>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
